import React, { useState, useContext, lazy, Suspense } from "react";
import { BrowserRouter, Route, Switch } from "react-router-dom";

//material-ui
import Container from "@material-ui/core/Container";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { AppBar, Toolbar, Typography, IconButton, Badge, Box } from "@material-ui/core";
import { Icon } from "../components/Icons";

//helpers
import clsx from "clsx";

//providers
import { ThemeContext } from "../providers/CustomThemeProvider";
import { AuthContext } from "../auth/AuthProvider";

//local
import PageButtons from "./PageButtons";
import NavDrawer, { drawerWidth } from "./NavDrawer";

import Home from "../pages/Home";
import Forbidden from "../pages/Forbidden";
import RequireAuth from "../auth/RequireAuth";

const Availability = lazy(() => import("../pages/Availability"));
const AvailabilityPost = lazy(() => import("../pages/Availability/post"));
const Images = lazy(() => import("../pages/Availability/images"));
const Settings = lazy(() => import("../pages/Settings"));
const Export = lazy(() => import("../pages/Availability/export"));
const Support = lazy(() => import("../pages/Support"));

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		display: "flex",
	},
	appBar: {
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
	},
	appBarShift: {
		width: `calc(100% - ${drawerWidth}px)`,
		marginLeft: drawerWidth,
		transition: theme.transitions.create(["margin", "width"], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	},
	menuButton: {
		marginRight: theme.spacing(2),
	},
	toolbarButtons: {
		marginLeft: "auto",
		display: "flex",
		flexDirection: "row-reverse",
	},
	hide: {
		display: "none",
	},
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
	},
	drawerHeader: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		// necessary for content to be below app bar
		...theme.mixins.toolbar,
		justifyContent: "flex-end",
	},
	navLink: {
		textDecoration: "none",
		color: "black",
	},
	content: {
		flexGrow: 1,
		padding: theme.spacing(3),
		transition: theme.transitions.create("margin", {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		marginLeft: drawerWidth,
	},
	contentShift: {
		transition: theme.transitions.create("margin", {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
		marginLeft: 0,
	},
	profile: {
		fontSize: ".8em",
		position: "absolute",
		backgroundColor: theme.palette.info.main,
	},
	iframe: {
		width: "100vw",
		height: "100%",
		border: "none",
		// Offset container padding
		margin: -theme.spacing(3),
		position: "absolute",
	},
}));

const Topbar = ({ updateAvailable, applyUpdate, setClientSet }: any) => {
	const classes = useStyles();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [pageTitle, setPageTitle] = useState("");
	const [pageButtons, setPageButtons] = useState([]);
	const { setTheme, themeType } = useContext(ThemeContext);
	const authContext = useContext(AuthContext);

	let profile = sessionStorage.getItem("profile") || "default";

	return (
		<>
			<BrowserRouter>
				{(window.location.protocol === "http:" && window.location.hostname !== "localhost") || (authContext.sessionChecked && !authContext.currentUser) ? (
					<AppBar id="appHeader" position="sticky">
						<Toolbar>
							<Typography variant="h6" noWrap>
								{pageTitle}
							</Typography>
						</Toolbar>
					</AppBar>
				) : (
					<AppBar
						id="appHeader"
						position="sticky"
						className={clsx(classes.appBar, {
							[classes.appBarShift]: drawerOpen,
						})}
					>
						{profile !== "default" ? <Box className={classes.profile}>&nbsp;Profile: {profile}&nbsp;</Box> : null}

						<Toolbar>
							<IconButton
								color="inherit"
								aria-label="open drawer"
								onClick={() => setDrawerOpen(true)}
								edge="start"
								className={clsx(classes.menuButton, drawerOpen && classes.hide)}
							>
								<Badge color="error" variant="dot" invisible={!updateAvailable}>
									<Icon name="bars" />
								</Badge>
							</IconButton>

							<Typography variant="h6" noWrap>
								{pageTitle}
							</Typography>
							<div className={classes.toolbarButtons}>
								<PageButtons pageButtons={pageButtons} />
							</div>
						</Toolbar>
						<NavDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} updateAvailable={updateAvailable} applyUpdate={applyUpdate} />
					</AppBar>
				)}
				<Suspense fallback={<div>Loading... </div>}>
					<Container
						maxWidth="xl"
						className={clsx([classes.content], {
							[classes.contentShift]: !drawerOpen,
						})}
					>
						<main>
							<Switch>
								<Route
									exact
									path="/forbidden"
									render={(props) => (
										<Forbidden {...props} setPageButtons={setPageButtons} setPageTitle={setPageTitle} setTheme={setTheme} themeType={themeType} />
									)}
								/>
								<Route
									exact
									path="/"
									render={(props) => (
										<RequireAuth>
											<Home {...props} setPageButtons={setPageButtons} setPageTitle={setPageTitle} setTheme={setTheme} themeType={themeType} />
										</RequireAuth>
									)}
								/>

								<Route
									exact
									path="/avail"
									render={(props) => (
										<RequireAuth>
											<Availability {...props} setPageButtons={setPageButtons} setPageTitle={setPageTitle} setTheme={setTheme} themeType={themeType} />
										</RequireAuth>
									)}
								/>
								<Route
									exact
									path="/availexport"
									render={(props) => <Export {...props} setPageButtons={setPageButtons} setPageTitle={setPageTitle} setTheme={setTheme} />}
								/>
								<Route
									exact
									path="/availpost"
									render={(props) => (
										<RequireAuth>
											<AvailabilityPost {...props} setPageButtons={setPageButtons} setPageTitle={setPageTitle} setTheme={setTheme} />
										</RequireAuth>
									)}
								/>
								<Route
									path="/availimages/:id"
									render={(props) => (
										<RequireAuth>
											<Images {...props} setPageButtons={setPageButtons} setPageTitle={setPageTitle} setTheme={setTheme} themeType={themeType} />
										</RequireAuth>
									)}
								/>
								<Route
									exact
									path="/settings"
									render={(props) => (
										<RequireAuth>
											<Settings
												{...props}
												setClientSet={setClientSet}
												setPageButtons={setPageButtons}
												setPageTitle={setPageTitle}
												setTheme={setTheme}
												themeType={themeType}
											/>
										</RequireAuth>
									)}
								/>
								<Route exact path="/support" render={() => <Support />} />
							</Switch>
						</main>
					</Container>
				</Suspense>
			</BrowserRouter>
		</>
	);
};

export default Topbar;
